import type {
  ProjectFileBatchTimelineEntry,
  ProjectFileState,
} from '@shared/models/project-file-batch.model';

/** One continuous period spent <em>in</em> {@link state} (wall clock from `started` → `completed`). */
export type ProjectFileTimelineSpan = {
  state: string;
  startMs: number;
  endMs: number;
};

/**
 * Build spans from paired `started` / `completed` events (same scope + `state`).
 * Unclosed `started` rows extend to `nowMs` (pipeline still in that state).
 */
export const buildTimelineSpans = (
  timeline: readonly ProjectFileBatchTimelineEntry[],
  predicate: (e: ProjectFileBatchTimelineEntry) => boolean,
  nowMs: number,
): ProjectFileTimelineSpan[] => {
  const ordered = [...timeline]
    .filter(predicate)
    .sort((a, b) => String(a.at).localeCompare(String(b.at)));

  const open = new Map<string, number>();
  const spans: ProjectFileTimelineSpan[] = [];

  for (const e of ordered) {
    const t = Date.parse(e.at);
    if (!Number.isFinite(t)) continue;
    if (e.event === 'started') {
      open.set(e.state, t);
      continue;
    }
    if (e.event !== 'completed') continue;
    const s0 = open.get(e.state);
    if (s0 === undefined) continue;
    if (t >= s0) spans.push({ state: e.state, startMs: s0, endMs: t });
    open.delete(e.state);
  }

  for (const [state, start] of open) {
    if (nowMs >= start) spans.push({ state, startMs: start, endMs: nowMs });
  }

  return spans.sort((a, b) => a.startMs - b.startMs);
};

export type TimelineSegmentLayout = {
  state: string;
  leftFrac: number;
  widthFrac: number;
};

/**
 * Normalize spans onto [0..1] for a horizontal bar: each segment gets `leftFrac`/`widthFrac`
 * of the union window `[minTs, maxTs]`.
 */
export const layoutTimelineSegments = (
  spans: readonly ProjectFileTimelineSpan[],
  minTs: number,
  maxTs: number,
): TimelineSegmentLayout[] => {
  const denom = Math.max(1e-9, maxTs - minTs);
  return spans.map((s) => ({
    state: s.state,
    leftFrac: (s.startMs - minTs) / denom,
    widthFrac: Math.max(0, (s.endMs - s.startMs) / denom),
  }));
};

export type PipelineTimelineExtentsOptions = {
  /**
   * When true, the axis ends at the last timeline/span time — wall-clock `nowMs`
   * is not used to stretch the range after the run finished.
   */
  pipelineComplete?: boolean;
};

/**
 * Single [min, max] ms window for batch + all file lanes so bars share one time axis.
 * By default includes `nowMs` so in-flight work can extend toward “now”; with
 * {@link PipelineTimelineExtentsOptions.pipelineComplete}, the upper bound stays at the
 * last recorded event/spans — your completed runs don’t drift to current wall clock.
 */
export const computePipelineTimelineExtents = (
  timeline: readonly ProjectFileBatchTimelineEntry[],
  spanGroups: readonly (readonly ProjectFileTimelineSpan[])[],
  nowMs: number,
  extraAnchorIso: readonly string[],
  options?: PipelineTimelineExtentsOptions,
): { minTs: number; maxTs: number } => {
  const pipelineComplete = options?.pipelineComplete ?? false;

  const dataNums: number[] = [];
  for (const iso of extraAnchorIso) {
    const u = Date.parse(iso);
    if (Number.isFinite(u)) dataNums.push(u);
  }
  for (const e of timeline) {
    const t = Date.parse(e.at);
    if (Number.isFinite(t)) dataNums.push(t);
  }
  for (const g of spanGroups) {
    for (const s of g) {
      dataNums.push(s.startMs, s.endMs);
    }
  }

  const finiteData = dataNums.filter(Number.isFinite);
  if (finiteData.length === 0) return { minTs: nowMs - 60_000, maxTs: nowMs };

  let minTs = Math.min(...finiteData);
  let maxTs = Math.max(...finiteData);

  if (!pipelineComplete) {
    minTs = Math.min(minTs, nowMs);
    maxTs = Math.max(maxTs, nowMs);
  }

  if (maxTs <= minTs) maxTs = minTs + 1000;
  return { minTs, maxTs };
};

/**
 * Clamp “current time” on the timeline for a playhead or tick mark: after completion,
 * use the extent’s historical end instead of advancing with wall clock.
 */
export const effectiveTimelinePlaybackMs = (
  wallNowMs: number,
  extentMinTs: number,
  extentMaxTs: number,
  pipelineComplete: boolean,
): number => {
  const t = pipelineComplete ? extentMaxTs : wallNowMs;
  return Math.min(extentMaxTs, Math.max(extentMinTs, t));
};

/**
 * Wall time spent in {@link phase} for {@link fileId}, from paired
 * timeline `started` / `completed` entries (same scope/state). Last completed
 * span wins if repeats exist. Non-finite durations return null.
 */
export const getProjectFilePhaseDurationMs = (
  timeline: readonly ProjectFileBatchTimelineEntry[],
  fileId: string,
  phase: ProjectFileState,
): number | null => {
  const stack: number[] = [];
  let lastMs: number | null = null;

  const ordered = [...timeline].sort((a, b) =>
    String(a.at).localeCompare(String(b.at)),
  );

  for (const e of ordered) {
    if (e.scope !== 'file' || e.fileId !== fileId) continue;
    if (e.state !== phase) continue;
    const t = Date.parse(e.at);
    if (!Number.isFinite(t)) continue;
    if (e.event === 'started') stack.push(t);
    if (e.event === 'completed') {
      const started = stack.pop();
      if (started === undefined) continue;
      const delta = t - started;
      if (!Number.isFinite(delta) || delta < 0) continue;
      lastMs = delta;
    }
  }

  return lastMs;
};

/** Compact label for durations from {@link getProjectFilePhaseDurationMs}. */
export const formatPhaseDurationMs = (ms: number): string =>
  ms < 1000 ? `${Math.round(ms)} ms` : `${(ms / 1000).toFixed(1)} s`;
